import { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/crmService';
import FundingWatch from './FundingWatch';

const TYPE_CONFIG = {
  ma:          { label: 'M&A',         stages: ['Identify','Research','Approach','LOI','Due Diligence','Negotiate','Close','Integrate'] },
  personnel:   { label: 'Personnel',   stages: ['Identify','Recruit','Interview','Offer','Onboarding','Active'] },
  capital:     { label: 'Capital',     stages: ['Identify','Pursue','Term Sheet','Due Diligence','Close','Deployed'] },
};

const NEED_TYPE_LABELS = {
  technical_hire: 'Hire', equipment: 'Equipment', facility: 'Facility',
  funding: 'Funding', partner: 'Partner', other: 'Other',
};

const PRIORITY_COLORS = { 1: '#EF4444', 2: '#F59E0B', 3: '#EAB308', 4: '#6B7280' };
const PRIORITY_LABELS = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low' };

const formatM = (v) => {
  if (!v) return null;
  const n = parseFloat(v);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n}`;
};

const stageKey = (s) => s.toLowerCase().replace(/\s+/g, '_');

const BLANK_FORM = {
  name: '', description: '', stage: '', estimated_value: '',
  target_close_date: '', priority: 3, notes: '', vertical_ids: [],
};

function ActionCard({ action, onClick }) {
  return (
    <div
      onClick={() => onClick(action)}
      style={{
        background: '#111827', border: '1px solid #1E2D45', borderRadius: '6px',
        padding: '10px 12px', cursor: 'pointer', marginBottom: '6px',
        borderLeft: `3px solid ${PRIORITY_COLORS[action.priority] || '#6B7280'}`,
        transition: 'border-color 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#22D3EE'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = PRIORITY_COLORS[action.priority] || '#6B7280'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px' }}>
        <div style={{
          width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, marginTop: '3px',
          background: PRIORITY_COLORS[action.priority] || '#6B7280',
        }} />
        <div style={{ color: '#F1F5F9', fontSize: '12px', fontWeight: '500', flex: 1, lineHeight: '1.3' }}>
          {action.name}
        </div>
      </div>
      {action.estimated_value && (
        <div style={{ color: '#22D3EE', fontSize: '11px', fontWeight: '600', marginBottom: '4px', paddingLeft: '14px' }}>
          {formatM(action.estimated_value)}
        </div>
      )}
      {action.target_close_date && (
        <div style={{ color: '#6B7280', fontSize: '10px', paddingLeft: '14px', marginBottom: '4px' }}>
          {new Date(action.target_close_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      )}
      {action.verticals?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', paddingLeft: '14px' }}>
          {action.verticals.slice(0, 3).map(v => (
            <span key={v.id} style={{
              background: '#22D3EE0F', color: '#22D3EE', border: '1px solid #22D3EE25',
              padding: '1px 6px', borderRadius: '8px', fontSize: '9px',
            }}>{v.name}</span>
          ))}
          {action.verticals.length > 3 && (
            <span style={{ color: '#4B5563', fontSize: '9px', paddingTop: '2px' }}>+{action.verticals.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
}

function ActionModal({ action, type, verticals, onClose, onSaved }) {
  const isEdit = !!action;
  const stages = TYPE_CONFIG[type]?.stages || [];
  const [form, setForm] = useState(() => isEdit ? {
    name: action.name || '',
    description: action.description || '',
    stage: action.stage || '',
    estimated_value: action.estimated_value || '',
    target_close_date: action.target_close_date ? action.target_close_date.slice(0, 10) : '',
    priority: action.priority || 3,
    notes: action.notes || '',
    vertical_ids: (action.verticals || []).map(v => v.id),
  } : { ...BLANK_FORM, stage: stages[0] || '', vertical_ids: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleVertical = (id) => {
    setForm(f => ({
      ...f,
      vertical_ids: f.vertical_ids.includes(id)
        ? f.vertical_ids.filter(x => x !== id)
        : [...f.vertical_ids, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        type,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        target_close_date: form.target_close_date || null,
      };
      if (isEdit) {
        await crmService.updateGrowthAction(action.id, payload);
        // sync verticals: add new, remove dropped
        const prev = (action.verticals || []).map(v => v.id);
        const toAdd = form.vertical_ids.filter(id => !prev.includes(id));
        const toDrop = prev.filter(id => !form.vertical_ids.includes(id));
        await Promise.all([
          ...toAdd.map(id => crmService.addGrowthVertical(action.id, id)),
          ...toDrop.map(id => crmService.removeGrowthVertical(action.id, id)),
        ]);
      } else {
        await crmService.createGrowthAction(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', background: '#0A0F1C', border: '1px solid #1E2D45',
    color: '#F1F5F9', padding: '7px 10px', borderRadius: '4px', fontSize: '12px',
    boxSizing: 'border-box',
  };
  const labelStyle = { color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '4px' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000000AA', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#111827', border: '1px solid #1E2D45', borderRadius: '8px',
        width: 'min(540px, 90vw)', maxHeight: '85vh', overflowY: 'auto', padding: '24px', margin: '0 auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#F1F5F9', fontSize: '15px', fontWeight: '600' }}>
            {isEdit ? 'Edit Action' : `New ${TYPE_CONFIG[type]?.label} Action`}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} required />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Stage *</label>
              <select style={inputStyle} value={form.stage} onChange={e => set('stage', e.target.value)} required>
                {stages.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <select style={inputStyle} value={form.priority} onChange={e => set('priority', parseInt(e.target.value))}>
                {[1,2,3,4].map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Estimated Value ($)</label>
              <input style={inputStyle} type="number" min="0" step="1000" value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label style={labelStyle}>Target Close Date</label>
              <input style={inputStyle} type="date" value={form.target_close_date} onChange={e => set('target_close_date', e.target.value)} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div>
            <label style={labelStyle}>Technology Verticals</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {verticals.map(v => {
                const checked = form.vertical_ids.includes(v.id);
                return (
                  <button
                    key={v.id} type="button"
                    onClick={() => toggleVertical(v.id)}
                    style={{
                      padding: '3px 10px', borderRadius: '10px', fontSize: '10px', cursor: 'pointer',
                      background: checked ? '#22D3EE18' : 'transparent',
                      color: checked ? '#22D3EE' : '#6B7280',
                      border: `1px solid ${checked ? '#22D3EE44' : '#1E2D45'}`,
                      fontWeight: checked ? '600' : '400',
                    }}
                  >{v.name}</button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notes</label>
            <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          {isEdit && (
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status || 'active'} onChange={e => set('status', e.target.value)}>
                {['active','on_hold','completed','cancelled'].map(s => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          )}

          {error && <div style={{ color: '#EF4444', fontSize: '11px' }}>{error}</div>}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '4px' }}>
            <button type="button" onClick={onClose} style={{
              padding: '7px 16px', background: 'none', border: '1px solid #1E2D45',
              color: '#94A3B8', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
            }}>Cancel</button>
            <button type="submit" disabled={saving} style={{
              padding: '7px 18px', background: saving ? '#0EA5E9' : '#22D3EE',
              color: '#0A0F1C', border: 'none', borderRadius: '4px',
              cursor: saving ? 'default' : 'pointer', fontSize: '12px', fontWeight: '700',
            }}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OperationalGapsStrip({ onPromoted }) {
  const [needs, setNeeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadNeeds = useCallback(() => {
    crmService.getOpenTechNeeds().then(setNeeds).catch(() => setNeeds([]));
  }, []);

  useEffect(() => { loadNeeds(); }, [loadNeeds]);

  const pending = needs.filter(n => !n.growth_action_id);
  if (needs.length === 0) return null;

  const promote = async (id) => {
    setBusyId(id);
    try {
      await crmService.promoteTechNeed(id);
      loadNeeds();
      onPromoted?.();
    } catch (e) { alert(e.message); } finally { setBusyId(null); }
  };

  return (
    <div style={{ borderBottom: '1px solid #1E2D45', background: '#c4a56308', flexShrink: 0 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', cursor: 'pointer' }}>
        <span style={{ color: '#c4a563', fontSize: 11, fontWeight: 700 }}>
          ⚠ {needs.length} operational gap{needs.length !== 1 ? 's' : ''} from Tusks
        </span>
        <span style={{ color: '#6B7280', fontSize: 10 }}>
          {pending.length > 0 ? `${pending.length} awaiting strategic assessment` : 'all in pipeline'}
        </span>
        <span style={{ marginLeft: 'auto', color: '#4B5563', fontSize: 10 }}>{open ? 'hide ▾' : 'show ▸'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 10px' }}>
          {needs.map(n => (
            <div key={n.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '1px solid #1E2D4566' }}>
              <span style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase', width: 70, flexShrink: 0 }}>{NEED_TYPE_LABELS[n.need_type] || n.need_type}</span>
              <span style={{ fontSize: 12, color: '#F1F5F9', flex: 1 }}>{n.description}</span>
              <span style={{ fontSize: 10, color: '#6B7280', flexShrink: 0 }}>{n.project_name}</span>
              <span style={{ fontSize: 9, color: PRIORITY_COLORS[Math.min(n.priority, 4)] || '#6B7280', fontWeight: 700, flexShrink: 0 }}>P{n.priority}</span>
              {n.growth_action_id ? (
                <span style={{ fontSize: 9, color: '#c4a563', border: '1px solid #c4a56344', borderRadius: 8, padding: '1px 8px', flexShrink: 0 }}>in pipeline</span>
              ) : (
                <button onClick={() => promote(n.id)} disabled={busyId === n.id}
                  style={{ background: '#c4a56318', border: '1px solid #c4a56344', color: '#c4a563', borderRadius: 8, padding: '2px 10px', cursor: 'pointer', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                  {busyId === n.id ? '…' : 'Assess → pipeline'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GrowthPipeline() {
  const [activeType, setActiveType] = useState('ma');
  const [actions, setActions] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAction, setEditingAction] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      crmService.getGrowthActions({ type: activeType }),
      verticals.length ? Promise.resolve(verticals) : crmService.getVerticals(),
    ])
      .then(([acts, verts]) => { setActions(acts); setVerticals(verts); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const stages = TYPE_CONFIG[activeType]?.stages || [];

  const totalValue = actions.reduce((s, a) => s + (parseFloat(a.estimated_value) || 0), 0);
  const verticalIds = new Set(actions.flatMap(a => (a.verticals || []).map(v => v.id)));

  const handleCardClick = (action) => {
    setEditingAction(action);
    setShowModal(true);
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditingAction(null);
    load();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Type switcher */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1E2D45', backgroundColor: '#0A0F1C', flexShrink: 0 }}>
        {[...Object.entries(TYPE_CONFIG), ['deal_flow', { label: 'Deal Flow' }]].map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setActiveType(key)}
            style={{
              padding: '9px 20px', background: 'none', border: 'none',
              borderBottom: activeType === key ? '2px solid #22D3EE' : '2px solid transparent',
              color: activeType === key ? '#22D3EE' : '#6B7280',
              cursor: 'pointer', fontSize: '12px', fontWeight: activeType === key ? '600' : '400',
            }}
          >{cfg.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        {activeType !== 'deal_flow' && (
          <button
            onClick={() => { setEditingAction(null); setShowModal(true); }}
            style={{
              margin: '6px 12px', padding: '4px 14px', background: '#22D3EE',
              color: '#0A0F1C', border: 'none', borderRadius: '4px',
              cursor: 'pointer', fontSize: '11px', fontWeight: '700',
            }}
          >+ Add</button>
        )}
      </div>

      {/* Operational gaps fed up from Tusks */}
      <OperationalGapsStrip onPromoted={load} />

      {/* Deal Flow view */}
      {activeType === 'deal_flow' && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <FundingWatch />
        </div>
      )}

      {/* Capacity roll-up + Kanban (non–Deal Flow views only) */}
      {activeType !== 'deal_flow' && (
        <>
          <div style={{
            display: 'flex', gap: '16px', padding: '8px 16px',
            backgroundColor: '#111827', borderBottom: '1px solid #1E2D45',
            alignItems: 'center', flexShrink: 0,
          }}>
            <span style={{ color: '#6B7280', fontSize: '11px' }}>
              <span style={{ color: '#F1F5F9', fontWeight: '600' }}>{actions.length}</span> actions active
            </span>
            <span style={{ color: '#1E2D45' }}>·</span>
            <span style={{ color: '#6B7280', fontSize: '11px' }}>
              <span style={{ color: '#22D3EE', fontWeight: '600' }}>{totalValue > 0 ? formatM(totalValue) : '$0'}</span> estimated value
            </span>
            <span style={{ color: '#1E2D45' }}>·</span>
            <span style={{ color: '#6B7280', fontSize: '11px' }}>
              <span style={{ color: '#F1F5F9', fontWeight: '600' }}>{verticalIds.size}</span> verticals impacted
            </span>
          </div>

          {/* Kanban */}
          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', fontSize: '13px' }}>
              Loading…
            </div>
          ) : (
            <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', display: 'flex', padding: '12px' }}>
              {stages.map(stage => {
                const key = stageKey(stage);
                const stageActions = actions.filter(a => stageKey(a.stage) === key || a.stage === stage);
                return (
                  <div key={stage} style={{ minWidth: '200px', maxWidth: '220px', marginRight: '10px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 8px 8px', flexShrink: 0,
                    }}>
                      <span style={{ color: '#94A3B8', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {stage}
                      </span>
                      {stageActions.length > 0 && (
                        <span style={{
                          background: '#1E2D45', color: '#6B7280', borderRadius: '10px',
                          padding: '1px 7px', fontSize: '10px', fontWeight: '600',
                        }}>{stageActions.length}</span>
                      )}
                    </div>
                    <div style={{
                      flex: 1, overflowY: 'auto', background: '#0D1520',
                      borderRadius: '6px', padding: '8px', minHeight: '80px',
                      border: '1px solid #1E2D45',
                    }}>
                      {stageActions.map(a => (
                        <ActionCard key={a.id} action={a} onClick={handleCardClick} />
                      ))}
                      {stageActions.length === 0 && (
                        <div style={{ color: '#1E2D45', fontSize: '10px', textAlign: 'center', paddingTop: '16px' }}>—</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Detail slide-out */}
      {selectedAction && !showModal && (
        <div style={{
          position: 'fixed', right: 0, top: 0, bottom: 0, width: 'clamp(220px, 50vw, 360px)',
          background: '#111827', borderLeft: '1px solid #1E2D45', zIndex: 500,
          overflowY: 'auto', padding: '20px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#F1F5F9', fontSize: '14px' }}>{selectedAction.name}</h3>
            <button onClick={() => setSelectedAction(null)} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '18px' }}>×</button>
          </div>
          <button
            onClick={() => { setEditingAction(selectedAction); setShowModal(true); }}
            style={{ padding: '5px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', marginBottom: '16px' }}
          >Edit</button>
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', fontSize: '11px' }}>
            {[
              ['Stage', selectedAction.stage],
              ['Priority', PRIORITY_LABELS[selectedAction.priority]],
              ['Value', formatM(selectedAction.estimated_value) || '—'],
              ['Close', selectedAction.target_close_date ? new Date(selectedAction.target_close_date).toLocaleDateString() : '—'],
              ['Status', selectedAction.status],
            ].map(([k, v]) => (
              <>
                <dt key={`k-${k}`} style={{ color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '10px' }}>{k}</dt>
                <dd key={`v-${k}`} style={{ margin: 0, color: '#F1F5F9' }}>{v}</dd>
              </>
            ))}
          </dl>
          {selectedAction.description && (
            <div style={{ marginTop: '14px' }}>
              <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Description</div>
              <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: '1.6' }}>{selectedAction.description}</div>
            </div>
          )}
          {selectedAction.notes && (
            <div style={{ marginTop: '14px' }}>
              <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Notes</div>
              <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: '1.6' }}>{selectedAction.notes}</div>
            </div>
          )}
          {selectedAction.verticals?.length > 0 && (
            <div style={{ marginTop: '14px' }}>
              <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Verticals</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {selectedAction.verticals.map(v => (
                  <span key={v.id} style={{
                    background: '#22D3EE14', color: '#22D3EE', border: '1px solid #22D3EE30',
                    padding: '2px 8px', borderRadius: '9px', fontSize: '10px',
                  }}>{v.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <ActionModal
          action={editingAction}
          type={editingAction?.type || activeType}
          verticals={verticals}
          onClose={() => { setShowModal(false); setEditingAction(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
